import { useInfiniteQuery } from '@tanstack/react-query';
import type { AccountHistoryEntry } from '../../types/accountHistory';
import type { Currency } from '../../types/account';
import { formatRelativeTime } from '../../lib/relativeTime';
import {
  changesToShow,
  describeChange,
  entrySummary,
  historyHorizonLine,
} from '../../lib/accountHistory';
import { Button } from '../../components/ui/button';
import { getAccountHistory } from './api';

// Account History tab (2026-09-29). Read-only feed of this account's audit
// trail, newest first, 200 per page. Brief:
// _architect/briefs/2026-09-29-account-history-tab.md.
//
// ⚠️ ON DEMAND: the tab content mounts only when the History tab is open
// (Radix unmounts inactive tabs), so the query runs only when someone looks.

// ⚠️ `currency` is the account's CURRENT currency, used to format budget
// changes. A change made while the account had a different currency renders
// with today's symbol. Accepted: the audit entry stores cents, not the currency
// they were in, and a currency change on an account is rare.
export function AccountHistorySection({ accountId, currency }: { accountId: string; currency: Currency | null }) {
  const query = useInfiniteQuery({
    queryKey: ['account-history', accountId],
    queryFn: ({ pageParam }) => getAccountHistory(accountId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const entries = query.data?.pages.flatMap((p) => p.entries) ?? [];

  return (
    <div className="space-y-4">
      {/* ⚠️ The two horizons. It LEADS the feed and renders in every state —
          loading, empty, error — because it is the one element whose absence
          nobody would notice. Asserted by AccountHistorySection.test.tsx. */}
      <p data-testid="history-horizon" className="text-sm text-slate-600">
        {historyHorizonLine()}
      </p>

      {query.isLoading ? (
        <p className="text-sm text-slate-400 italic">Loading history…</p>
      ) : query.error ? (
        <p className="text-sm text-red-600">
          Failed to load history: {query.error instanceof Error ? query.error.message : 'unknown error'}
        </p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-slate-400 italic">No history yet.</p>
      ) : (
        <ol className="space-y-4">
          {entries.map((e) => (
            <HistoryRow key={e.id} entry={e} currency={currency} />
          ))}
        </ol>
      )}

      {query.hasNextPage && (
        <Button
          variant="outline"
          size="sm"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? 'Loading…' : 'Load older'}
        </Button>
      )}
    </div>
  );
}

function HistoryRow({ entry, currency }: { entry: AccountHistoryEntry; currency: Currency | null }) {
  const changes = changesToShow(entry);
  return (
    <li className="grid grid-cols-[140px_1fr] gap-3 text-sm">
      <time
        dateTime={entry.timestamp}
        title={new Date(entry.timestamp).toLocaleString()}
        className="text-xs text-slate-500 pt-0.5"
      >
        {formatRelativeTime(entry.timestamp)}
      </time>
      <div className="text-slate-800 min-w-0">
        <p>
          <span className="font-medium">{entrySummary(entry)}</span>
          <span className="text-slate-500"> · {entry.actor}</span>
        </p>
        {entry.context && <p className="text-xs text-slate-500">{entry.context}</p>}
        {changes.length > 0 && (
          <ul className="mt-1 list-disc list-inside space-y-0.5 text-slate-600 text-xs break-words">
            {changes.map((c) => (
              <li key={c.field}>{describeChange(c, entry.operation, currency)}</li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}
